import { useCallback, useEffect, useRef, useState } from "react"
import type { GrantRole, ISharePanelHook, OrgUser, ShareGrantOutcome, ShareTeam } from "../components/share/types"
import {
  CreatedShareGrantSchema,
  ResourceSharesSchema,
  ShareableTeamsPageSchema,
  ShareUsersSchema,
  type ShareableTeamsPage,
} from "../schemas/resource-shares"

export interface IShareRequestInit {
  method: "POST" | "DELETE"
  body?: string
}

export type ShareFetchJson = (path: string, init?: IShareRequestInit) => Promise<unknown>

export type ShareGrantChange = "added" | "removed"

const TEAMS_PAGE_SIZE = 100

const toShareTeam = (team: ShareableTeamsPage["teams"][number]): ShareTeam => ({ teamId: team.id, name: team.name, memberCount: team.memberCount })

const teamsQuery = (page: number) => `/api/organizations/teams?limit=${TEAMS_PAGE_SIZE}&page=${page}`

export async function fetchShareableTeams(fetchJson: ShareFetchJson): Promise<ShareTeam[]> {
  const teams: ShareTeam[] = []
  for (let page = 1; ; page += 1) {
    const data = ShareableTeamsPageSchema.parse(await fetchJson(teamsQuery(page)))
    teams.push(...data.teams.filter((team) => team.canShareTo).map(toShareTeam))
    if (page * TEAMS_PAGE_SIZE >= data.total || data.teams.length === 0) return teams
  }
}

export interface IResourceSharePanelConfig {
  resourceId: string
  // Path prefix for the resource's share endpoints, e.g. "/api/files/user",
  // "/api/prompts/user", "/api/projects". Grants live at
  // `${basePath}/${resourceId}/shares[/${grantId}]`.
  basePath: string
  currentUser: OrgUser | null
  fetchJson: ShareFetchJson
  onGrantsChanged?: (change: ShareGrantChange) => void
  prefetchShareableTeams?: boolean
}

export interface IResourceSharePanel extends ISharePanelHook {
  shareLoadError: string | null
  onAddTeam: (team: ShareTeam, role: GrantRole) => Promise<ShareGrantOutcome>
  onRemoveTeam: (team: ShareTeam) => Promise<void>
}

// Backs every resource's share panel (files, prompts, skills, projects, clauses)
// in the web app and the Word add-in. All share endpoints follow the same
// shape, so the only per-resource inputs are the `basePath` and the resource id.
export function useResourceSharePanel({
  resourceId,
  basePath,
  currentUser,
  fetchJson,
  onGrantsChanged,
  prefetchShareableTeams = true,
}: IResourceSharePanelConfig): IResourceSharePanel {
  const [initialSharedUsers, setInitialSharedUsers] = useState<OrgUser[] | undefined>(undefined)
  const [shareLoadError, setShareLoadError] = useState<string | null>(null)
  const [initialSharedTeams, setInitialSharedTeams] = useState<ShareTeam[]>([])
  const [owner, setOwner] = useState<OrgUser | null>(null)
  const [shareableTeams, setShareableTeams] = useState<ShareTeam[]>([])
  const grantsByUserIdRef = useRef<Map<string, string>>(new Map())
  const grantsByTeamIdRef = useRef<Map<string, string>>(new Map())

  useEffect(() => {
    let active = true
    fetchJson(`${basePath}/${resourceId}/shares`)
      .then((response) => {
        if (!active) return
        const data = ResourceSharesSchema.parse(response)
        // Build the next grants map alongside the visible users and swap it in
        // atomically with the state update, so the map is never out of sync
        // with the rendered list (a removal in the gap would miss its grant).
        const users: OrgUser[] = []
        const nextGrantsByUserId = new Map<string, string>()
        for (const grant of data.grants) {
          if (grant.user) {
            users.push(grant.user)
            nextGrantsByUserId.set(grant.granteeUserId, grant.id)
          }
        }
        const teams: ShareTeam[] = []
        const nextGrantsByTeamId = new Map<string, string>()
        for (const grant of data.teamGrants) {
          if (grant.team) {
            teams.push({
              teamId: grant.granteeTeamId,
              name: grant.team.name,
              memberCount: grant.team.memberCount,
              role: grant.role,
            })
            nextGrantsByTeamId.set(grant.granteeTeamId, grant.id)
          }
        }
        grantsByUserIdRef.current = nextGrantsByUserId
        grantsByTeamIdRef.current = nextGrantsByTeamId
        setOwner(data.owner)
        setInitialSharedUsers(users)
        setInitialSharedTeams(teams)
        setShareLoadError(null)
      })
      .catch((err) => {
        if (!active) return
        console.error("Failed to load resource shares:", err)
        setShareLoadError((err as Error)?.message || "Failed to load who has access")
      })
    return () => { active = false }
  }, [fetchJson, basePath, resourceId])

  const searchUsers = useCallback(async (query: string): Promise<OrgUser[]> => {
    return ShareUsersSchema.parse(await fetchJson(`/api/organizations/users?scope=teammates&search=${encodeURIComponent(query)}`))
  }, [fetchJson])

  useEffect(() => {
    if (!prefetchShareableTeams) return
    let active = true
    fetchShareableTeams(fetchJson)
      .then((teams) => {
        if (active) setShareableTeams(teams)
      })
      .catch((err) => {
        if (active) console.error("Failed to load shareable teams:", err)
      })
    return () => { active = false }
  }, [fetchJson, prefetchShareableTeams])

  const onAddUser = useCallback(async (user: OrgUser, role: GrantRole): Promise<ShareGrantOutcome> => {
    const outcome: ShareGrantOutcome = grantsByUserIdRef.current.has(user.userId) ? "role_changed" : "invited"
    const grant = CreatedShareGrantSchema.parse(await fetchJson(`${basePath}/${resourceId}/shares`, {
      method: "POST",
      body: JSON.stringify({ granteeUserId: user.userId, role }),
    }))
    grantsByUserIdRef.current.set(user.userId, grant.id)
    onGrantsChanged?.("added")
    return outcome
  }, [fetchJson, basePath, resourceId, onGrantsChanged])

  const onRemoveUser = useCallback(async (user: OrgUser) => {
    const grantId = grantsByUserIdRef.current.get(user.userId)
    // A visible shared user without a known grant id means our local mapping
    // is out of sync — fail loudly instead of silently no-opping the revoke.
    if (!grantId) {
      throw new Error(`Missing share grant for user ${user.userId}`)
    }
    await fetchJson(`${basePath}/${resourceId}/shares/${grantId}`, { method: "DELETE" })
    grantsByUserIdRef.current.delete(user.userId)
    onGrantsChanged?.("removed")
  }, [fetchJson, basePath, resourceId, onGrantsChanged])

  const onAddTeam = useCallback(async (team: ShareTeam, role: GrantRole): Promise<ShareGrantOutcome> => {
    const outcome: ShareGrantOutcome = grantsByTeamIdRef.current.has(team.teamId) ? "role_changed" : "invited"
    const grant = CreatedShareGrantSchema.parse(await fetchJson(`${basePath}/${resourceId}/shares`, {
      method: "POST",
      body: JSON.stringify({ granteeTeamId: team.teamId, role }),
    }))
    grantsByTeamIdRef.current.set(team.teamId, grant.id)
    onGrantsChanged?.("added")
    return outcome
  }, [fetchJson, basePath, resourceId, onGrantsChanged])

  const onRemoveTeam = useCallback(async (team: ShareTeam) => {
    const grantId = grantsByTeamIdRef.current.get(team.teamId)
    if (!grantId) {
      throw new Error(`Missing share grant for team ${team.teamId}`)
    }
    await fetchJson(`${basePath}/${resourceId}/shares/${grantId}`, { method: "DELETE" })
    grantsByTeamIdRef.current.delete(team.teamId)
    onGrantsChanged?.("removed")
  }, [fetchJson, basePath, resourceId, onGrantsChanged])

  return {
    currentUser,
    owner,
    searchUsers,
    initialSharedUsers,
    initialSharedTeams,
    shareableTeams,
    shareLoadError,
    onAddUser,
    onRemoveUser,
    onAddTeam,
    onRemoveTeam,
  }
}
